import React, { useEffect, useState } from 'react';
import { useResumeContext } from '../../context/ResumeContext';
import { motion } from 'framer-motion';

/**
 * ConclusionModule Component
 * 
 * Provides a personalized conclusion based on sections explored,
 * offers interactive contact options, and downloadable resume formats.
 */
const ConclusionModule = () => {
  const { markSectionViewed, progress, viewedSections } = useResumeContext();
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  
  // Mark this section as viewed when component mounts
  useEffect(() => {
    markSectionViewed('conclusion');
  }, [markSectionViewed]);

  // Animation variants for motion components
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  /**
   * Returns a personalized message based on progress
   */
  const getEngagementMessage = () => {
    if (progress < 25) return "Just getting started! Explore more sections to see my full qualifications.";
    if (progress < 50) return "You're making progress! Continue exploring to learn more about my capabilities.";
    if (progress < 75) return "Good exploration! You've discovered much of my experience and skills.";
    if (progress < 100) return "Excellent engagement! You've explored most of my qualifications.";
    return "Complete review! You've thoroughly explored my experience and capabilities.";
  };

  /**
   * Get personalized insights based on which sections were viewed
   */
  const getPersonalizedInsights = () => {
    const insights = [];
    
    if (viewedSections.framework) {
      insights.push({
        title: "Instructional Design Approach",
        content: "You've explored my instructional design framework, which demonstrates my structured approach to creating educational content. This methodology ensures that my technical documentation and learning materials are systematic, comprehensible, and genuinely effective.",
        icon: "🔍",
        color: "blue"
      });
    }
    
    if (viewedSections.career) {
      insights.push({
        title: "Professional Journey",
        content: "By reviewing my career journey, you've seen how my experience has built upon itself to create a unique perspective on technical education. Each role has contributed to my ability to translate complex information into accessible learning experiences.",
        icon: "🚀",
        color: "green"
      });
    }
    
    if (viewedSections.competency) {
      insights.push({
        title: "Skill Assessment",
        content: "You've examined my technical and instructional skills assessment, showing my strengths in areas critical to effective educational content development. This self-evaluation demonstrates my commitment to continuous growth and transparent communication.",
        icon: "📊",
        color: "purple"
      });
    }
    
    if (viewedSections.portfolio) {
      insights.push({
        title: "Practical Applications",
        content: "Through my portfolio, you've seen practical examples of how I apply my instructional design principles to real-world technical challenges. These projects showcase my ability to create engaging, effective learning experiences across different topics and formats.",
        icon: "💼",
        color: "orange"
      });
    }
    
    return insights.length > 0 ? insights : [{
      title: "Resume Overview",
      content: "Based on the sections you've explored, this interactive resume demonstrates my approach to transforming complex technical information into engaging learning experiences. I encourage you to explore more sections to get a complete picture of my qualifications.",
      icon: "📚",
      color: "teal"
    }];
  };
  
  // Resume download formats
  const resumeFormats = [
    { id: 'pdf', label: 'PDF', icon: '📄', filename: 'Alexander_Donoho_Resume.pdf' },
    { id: 'docx', label: 'Word', icon: '📝', filename: 'Alexander_Donoho_Resume.docx' },
    { id: 'txt', label: 'Plain Text', icon: '📋', filename: 'Alexander_Donoho_Resume.txt' }
  ];

  return (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.section variants={itemVariants} className="card border-l-4 border-teal-500 overflow-hidden">
        <h2 className="text-xl font-bold text-teal-700 mb-4 flex items-center gap-2">
          ✅ Summary Assessment
        </h2>
        <p className="mb-6">
          Thank you for exploring my interactive resume! This final section provides a personalized summary 
          of my qualifications based on the areas you've explored, along with a self-assessment of my readiness 
          for instructional design roles.
        </p>
        
        <div className="bg-teal-50 p-4 rounded-lg mb-6">
          <h3 className="font-bold text-teal-800 mb-2">Engagement Progress</h3>
          <div className="w-full h-4 bg-teal-100 rounded-full mb-2">
            <motion.div 
              className="h-4 bg-teal-500 rounded-full" 
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            ></motion.div>
          </div>
          <p className="text-teal-700">{getEngagementMessage()}</p>
        </div>
      </motion.section>
      
      <motion.section variants={itemVariants} className="card">
        <h3 className="text-lg font-bold mb-4">Personalized Insights</h3>
        <div className="space-y-4">
          {getPersonalizedInsights().map((insight, index) => (
            <motion.div 
              key={index}
              className={`border-l-4 border-${insight.color}-500 pl-4`}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 + 0.3 }}
            >
              <h4 className={`font-bold text-${insight.color}-800 flex items-center gap-2`}>
                <span>{insight.icon}</span> {insight.title}
              </h4>
              <p className="text-slate-600 mt-1">
                {insight.content}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>
      
      <motion.section variants={itemVariants} className="card">
        <h3 className="text-lg font-bold mb-4">Key Qualifications Summary</h3>
        <div className="space-y-4">
          <motion.div 
            className="border-l-4 border-blue-500 pl-4"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="font-bold text-blue-800">7+ Years Creating Technical Educational Content</h4>
            <p className="text-slate-600 mt-1">
              Extensive experience developing comprehensive technical documentation, training materials, 
              and educational resources across multiple industries and subject areas.
            </p>
          </motion.div>
          <motion.div 
            className="border-l-4 border-green-500 pl-4"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="font-bold text-green-800">Complex Technical Simplification Skills</h4>
            <p className="text-slate-600 mt-1">
              Demonstrated ability to transform highly technical concepts into accessible, 
              engaging learning materials suitable for diverse audiences with varying levels of 
              technical background.
            </p>
          </motion.div>
          <motion.div 
            className="border-l-4 border-purple-500 pl-4"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="font-bold text-purple-800">Multimedia Training Development</h4>
            <p className="text-slate-600 mt-1">
              Experience creating and delivering training through multiple formats including 
              written documentation, webinars, workshops, and interactive learning tools.
            </p>
          </motion.div>
          <motion.div 
            className="border-l-4 border-orange-500 pl-4"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h4 className="font-bold text-orange-800">Visual Learning Design Expertise</h4>
            <p className="text-slate-600 mt-1">
              Strong skills in creating visual learning aids including diagrams, process 
              visualizations, flowcharts, and other graphic elements that enhance understanding.
            </p>
          </motion.div>
        </div>
      </motion.section>
      
      <motion.section variants={itemVariants} className="card">
        <h3 className="text-lg font-bold mb-4">Resume Download</h3>
        <div className="p-4 border rounded-lg bg-slate-50">
          <div className="p-4 bg-white rounded-lg border mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold">Alexander Donoho Resume</h4>
                <p className="text-sm text-slate-500">
                  PDF Format - Best for printing and sharing
                </p>
              </div>
              <a 
                href="https://www.madlab.science/s/Donoho_Education_Resume_2025.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Download PDF
              </a>
            </div>
          </div>
        </div>
      </motion.section>
      
      <motion.section variants={itemVariants} className="card">
        <h3 className="text-lg font-bold mb-4">Next Steps</h3>
        <div className="p-4 border rounded-lg">
          <p className="mb-3">
            I'm excited about the opportunity to apply my technical education experience to instructional design
            roles. I'm particularly interested in positions that would allow me to:
          </p>
          <ul className="space-y-2">
            <motion.li 
              className="flex items-start gap-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <span className="text-teal-500">✓</span>
              <span>Create comprehensive learning experiences for technical subjects</span>
            </motion.li>
            <motion.li 
              className="flex items-start gap-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="text-teal-500">✓</span>
              <span>Develop visual and interactive learning tools</span>
            </motion.li>
            <motion.li 
              className="flex items-start gap-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className="text-teal-500">✓</span>
              <span>Transform complex processes into accessible training materials</span>
            </motion.li>
            <motion.li 
              className="flex items-start gap-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <span className="text-teal-500">✓</span>
              <span>Continue expanding my skills in e-learning development</span>
            </motion.li>
          </ul>
          
          <div className="mt-4 p-3 bg-teal-50 rounded-lg">
            <p className="font-medium text-teal-800">
              I welcome the opportunity to discuss how my experience could benefit your team and how
              I can continue to grow in the instructional design field.
            </p>
          </div>
          
          {/* Contact Card */}
          <div className="mt-6 bg-white rounded-lg border p-4">
            <h4 className="font-bold mb-2">Get in Touch</h4>
            <div>
              <p className="mb-4">
                I'd love to connect with you about opportunities or answer any questions you may have.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <motion.a 
                  href="mailto:alex@madlab.science" 
                  className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-center flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>✉️</span> Email Me
                </motion.a>
                <motion.a 
                  href="https://www.linkedin.com/in/alexanderdonoho" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors text-center flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>👥</span> LinkedIn
                </motion.a>
                <motion.a 
                  href="https://www.madlab.science/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-center flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>📝</span> Contact Form
                </motion.a>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </motion.div>
  );
};

export default ConclusionModule;
